// src/share/EmailPillInput.jsx
//
// Multi-email entry field with Gmail-style pills. Used in all four share
// modals (creation / profile / company / team) for the "To:" recipient list.
//
// Behavior:
//   - Type an email, press Enter / comma / semicolon → committed as a pill
//   - Backspace on empty input → removes last pill
//   - Click the X on a pill → removes that pill
//   - Paste a comma-/whitespace-list → splits and validates each piece
//   - Invalid email + Enter → red border + helper text, no pill created
//   - On blur, valid trailing text auto-commits silently (no red border);
//     invalid trailing text just stays in the input
//   - On parent submit, parent calls `ref.current.commitPending()` to flush
//     any trailing text. Returns true if committed (or empty), false if the
//     trailing text is invalid — in which case the red border shows and
//     parent should abort submit.
//
// Email validation is RFC-light but TLD-strict: requires `name@domain.tld`
// with a 2-to-63-letter TLD. Catches the common "I forgot the .com" mistake
// that prompted this whole component (Brett shipping the Email video bake
// 2026-05-29 and re-examining the recipient flow).
//
// Pill style intentionally matches CustomerPicker's pills (same modal,
// same visual language) so a recipient list mixed across both inputs reads
// as one cohesive set.

import React, { useState, forwardRef, useImperativeHandle, useCallback } from 'react';

// `name@domain.tld` with TLD = 2-63 letters. Rejects `brett@ehandshake`
// (no TLD), `@example.com` (no local part), `brett.com` (no @).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,63}$/;

function isValidEmail(s) {
  return EMAIL_REGEX.test(String(s || '').trim());
}

const EmailPillInput = forwardRef(function EmailPillInput(
  {
    value = [],
    onChange,
    label,
    placeholder = 'email@example.com — press Enter to add',
    ariaLabel,
  },
  ref
) {
  const [inputText, setInputText] = useState('');
  const [showInvalid, setShowInvalid] = useState(false);

  // Try to commit the current input text as a pill.
  // Returns the new pills array if either: empty (nothing to commit) OR
  // valid (committed). Returns null if non-empty and invalid — sets red
  // border state. Returning the array directly (not just a boolean) lets
  // the parent's submit handler use it synchronously without waiting for
  // React's state update to flush.
  const commitCurrent = useCallback((opts = { showRedOnFail: true }) => {
    const trimmed = inputText.trim().replace(/[,;]+$/, '').trim();
    if (!trimmed) {
      setShowInvalid(false);
      return value;
    }
    if (!isValidEmail(trimmed)) {
      if (opts.showRedOnFail) setShowInvalid(true);
      return null;
    }
    const nextPills = value.includes(trimmed) ? value : [...value, trimmed];
    if (nextPills !== value) onChange?.(nextPills);
    setInputText('');
    setShowInvalid(false);
    return nextPills;
  }, [inputText, value, onChange]);

  // Parent calls this before submitting so any in-progress text becomes
  // a pill (or red-borders if invalid). Returns the up-to-date pills
  // array on success, null on failure (invalid trailing text).
  useImperativeHandle(ref, () => ({
    commitPending: () => commitCurrent({ showRedOnFail: true }),
  }), [commitCurrent]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      commitCurrent({ showRedOnFail: true });
    } else if (e.key === 'Backspace' && !inputText && value.length > 0) {
      // Backspace on empty input → remove last pill (Gmail behavior)
      onChange?.(value.slice(0, -1));
    }
  };

  const handleChange = (e) => {
    setInputText(e.target.value);
    if (showInvalid) setShowInvalid(false);
  };

  // Silent auto-commit on blur — if the trailing text is a valid email,
  // pill it; if not, leave it alone (no red border). User can come back
  // and finish typing. Hard validation only happens on Enter or Submit.
  const handleBlur = () => {
    commitCurrent({ showRedOnFail: false });
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (!/[,;\s\n]/.test(pasted)) return; // single-token paste — let it fill normally
    e.preventDefault();
    const tokens = pasted.split(/[,;\s\n]+/).map(s => s.trim()).filter(Boolean);
    const seen = new Set(value);
    const newValid = [];
    let trailingInvalid = '';
    for (const t of tokens) {
      if (isValidEmail(t)) {
        if (!seen.has(t)) {
          seen.add(t);
          newValid.push(t);
        }
      } else {
        // Keep the last invalid token in the input so the user sees + fixes it
        trailingInvalid = t;
      }
    }
    if (newValid.length) onChange?.([...value, ...newValid]);
    setInputText(trailingInvalid);
    setShowInvalid(false);
  };

  const removePill = (email) => {
    onChange?.(value.filter(e => e !== email));
  };

  const borderClass = showInvalid
    ? 'border-red-400 ring-2 ring-red-200'
    : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#00aef7]';

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className={`flex flex-wrap items-center gap-1.5 p-2 border rounded text-sm bg-white ${borderClass}`}>
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[#00aef7]/10 text-[#00aef7] border border-[#00aef7]/20 rounded-full"
          >
            {email}
            <button
              onClick={() => removePill(email)}
              className="ml-0.5 text-[#00aef7]/60 hover:text-[#00aef7] text-sm leading-none"
              title="Remove"
              type="button"
              aria-label={`Remove ${email}`}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={ariaLabel || label || 'Email recipient'}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          className="flex-1 min-w-[140px] outline-none bg-transparent text-sm"
        />
      </div>
      {showInvalid && (
        <p className="mt-1 text-xs text-red-600">
          That doesn't look like a valid email — needs a name, @, domain, and a TLD (like .com / .net / .io).
        </p>
      )}
    </div>
  );
});

export default EmailPillInput;
